import { useEffect } from 'react'
import {
  BRAND,
  DEFAULT_OG_IMAGE,
  SITE_URL,
} from '../data/site'

interface DocumentMeta {
  title: string
  description: string
  /** Route path, e.g. `/shop`. Combined with SITE_URL for the canonical. */
  path: string
  ogImage?: string
  ogImageAlt?: string
  /** Confirmation and error pages are never indexed. */
  noindex?: boolean
}

function setMetaTag(
  attribute: 'name' | 'property',
  key: string,
  content: string,
) {
  let tag = document.head.querySelector<HTMLMetaElement>(
    `meta[${attribute}="${key}"]`,
  )
  if (!tag) {
    tag = document.createElement('meta')
    tag.setAttribute(attribute, key)
    document.head.appendChild(tag)
  }
  tag.setAttribute('content', content)
}

/**
 * Sets the document title, description, canonical and Open Graph tags for
 * the current route.
 *
 * IMPORTANT — this runs after React mounts, which is too late for social
 * and messaging scrapers: none of them execute JavaScript, so a shared link
 * will unfurl as a bare URL no matter how correct this looks in devtools.
 * Shipping these tags in the initial HTML response is a launch blocker
 * recorded in docs/registry.md as I01.
 */
export function useDocumentMeta({
  title,
  description,
  path,
  ogImage = DEFAULT_OG_IMAGE.src,
  ogImageAlt = DEFAULT_OG_IMAGE.alt,
  noindex = false,
}: DocumentMeta) {
  useEffect(() => {
    const url = `${SITE_URL}${path}`
    document.title = title

    // Screen readers take pronunciation from lang. Set on the served
    // document too — this is a fallback, not the real fix.
    if (document.documentElement.lang !== 'en-GB') {
      document.documentElement.lang = 'en-GB'
    }

    setMetaTag('name', 'description', description)
    setMetaTag(
      'name',
      'robots',
      noindex ? 'noindex, follow' : 'index, follow',
    )

    setMetaTag('property', 'og:title', title)
    setMetaTag('property', 'og:description', description)
    setMetaTag('property', 'og:url', url)
    setMetaTag('property', 'og:type', 'website')
    setMetaTag('property', 'og:site_name', BRAND.name)
    setMetaTag('property', 'og:image', ogImage)
    setMetaTag('property', 'og:image:alt', ogImageAlt)

    setMetaTag('name', 'twitter:card', 'summary_large_image')
    setMetaTag('name', 'twitter:title', title)
    setMetaTag('name', 'twitter:description', description)
    setMetaTag('name', 'twitter:image', ogImage)

    let canonical = document.head.querySelector<HTMLLinkElement>(
      'link[rel="canonical"]',
    )
    if (!canonical) {
      canonical = document.createElement('link')
      canonical.rel = 'canonical'
      document.head.appendChild(canonical)
    }
    canonical.href = url
  }, [title, description, path, ogImage, ogImageAlt, noindex])
}
